import { Link } from "react-router-dom";
import { ArrowRight, Car, CreditCard, DollarSign, Home, Lightbulb, ShoppingCart, Upload } from "lucide-react";
import { Card } from "@/components/ui/card";
import type { PortalRequiredDocument } from "@/features/portal/api";
import { portalHref } from "@/features/portal/portal-base";

const CATEGORIES = [
  { icon: Home, label: "Housing" },
  { icon: Car, label: "Car" },
  { icon: ShoppingCart, label: "Groceries" },
  { icon: Lightbulb, label: "Utilities" },
];

/** "What we still need from you" — entry-point task cards for the client's
 * outstanding work. Each card is a link into the real flow; the dropzone and
 * category tiles are navigational affordances, not inline uploaders. */
export function NeedsTasks({
  pendingDocs,
  showFinancials,
  firmName,
}: {
  pendingDocs: PortalRequiredDocument[];
  showFinancials: boolean;
  firmName: string;
}) {
  const docCount = pendingDocs.length;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-foreground">What we still need from you</h2>
          <p className="text-muted-foreground mt-1">
            Don't worry about getting it perfect — we'll check everything for you.
          </p>
        </div>
        {firmName && (
          <span className="text-muted-foreground text-sm">
            Requested by <strong className="text-foreground">{firmName}</strong>, your tax team
          </span>
        )}
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        {docCount > 0 && (
          <Link
            to={portalHref("/requested-documents")}
            className="hover:border-primary hover:shadow-card-hover flex flex-col gap-4 rounded-xl border-2 bg-card p-6 transition-colors"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="bg-accent text-accent-foreground grid size-14 place-items-center rounded-sm">
                <CreditCard className="size-7" />
              </div>
              <span className="bg-primary-soft text-warning rounded-full px-3 py-1.5 text-sm font-bold">
                {docCount} to upload
              </span>
            </div>
            <div>
              <div className="font-heading text-xl font-bold text-foreground">
                Upload your requested documents
              </div>
              <p className="text-muted-foreground mt-1.5 text-sm">
                {pendingDocs
                  .slice(0, 3)
                  .map((d) => d.name)
                  .join(", ")}
                {docCount > 3 ? ", and more" : ""}. A PDF or a clear photo of each is fine.
              </p>
            </div>
            <div className="bg-muted/40 flex items-center gap-3.5 rounded-lg border-2 border-dashed p-4">
              <div className="bg-card text-success grid size-11 place-items-center rounded-sm border">
                <Upload className="size-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-foreground">
                  Drop files here, or <span className="text-info underline">choose your files</span>
                </div>
                <div className="text-muted-foreground text-xs">PDF, JPG or PNG · Up to 25 MB each</div>
              </div>
            </div>
          </Link>
        )}

        {showFinancials && (
          <Link
            to={portalHref("/financials/personal")}
            className="hover:border-primary hover:shadow-card-hover flex flex-col gap-4 rounded-xl border-2 bg-card p-6 transition-colors"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="bg-success-soft text-success grid size-14 place-items-center rounded-sm">
                <DollarSign className="size-7" />
              </div>
              <span className="bg-primary-soft text-warning rounded-full px-3 py-1.5 text-sm font-bold">
                about 5 minutes
              </span>
            </div>
            <div>
              <div className="font-heading text-xl font-bold text-foreground">
                Tell us your monthly income &amp; bills
              </div>
              <p className="text-muted-foreground mt-1.5 text-sm">
                Simple questions, one at a time — rent, groceries, car, utilities. Rough numbers are
                okay, and you can save and come back any time.
              </p>
            </div>
            <div className="grid grid-cols-4 gap-2.5">
              {CATEGORIES.map(({ icon: Icon, label }) => (
                <div key={label} className="bg-muted/40 rounded-md p-3 text-center">
                  <Icon className="text-success mx-auto size-5" />
                  <div className="mt-1 text-xs font-semibold text-foreground">{label}</div>
                </div>
              ))}
            </div>
            <div className="mt-auto flex items-center justify-between gap-3">
              <span className="text-muted-foreground text-xs">
                Why we ask: the IRS uses this to decide what you can afford.
              </span>
              <span className="text-info inline-flex shrink-0 items-center gap-1.5 text-sm font-bold">
                Start <ArrowRight className="size-4" />
              </span>
            </div>
          </Link>
        )}
      </div>
    </section>
  );
}
